import { DropdownMenuItemType, IDropdownOption } from '@fluentui/react';
import { useCallback, useMemo, useState } from 'react';
import { IFieldConfig, IFieldRenderProps, IFormSchema, useFormContext, useValidation } from 'spark-forms';
import { CustomProperty } from '../../../../api/generated/data-contracts';
import { FilterDropdown } from '../../../../_components/FilterDropdown/FilterDropdown';
import { schemaHelper } from '../helpers/schemaHelper';
import { DropdownOptionEx } from '../../../../Utilities/DropdownOptionEx';
import { useTranslation } from '../../../../Locales/i18n/localizationService';

export interface IEntityFieldSelectorProps<T> {
    schema: () => IFormSchema<T>;
    customProperties?: () => CustomProperty[];
	multiSelect?: boolean;
	propertyTypes: "fields" | "properties" | "both";
}

export const EntityFieldSelector: React.FC<any> = <T,>({
    value,
    disabled,
    fieldName,
    options,
	path,
}: IFieldRenderProps<T, IFieldConfig<IEntityFieldSelectorProps<T>>, string | number | string[] | number[] | undefined>) => {
	const { t } = useTranslation();
    const { onChange: onChangeForm } = useFormContext();
	const { validate, validationMessage } = useValidation(path);

    const [localValue, setLocalValue] = useState<string | number | string[] | number[]>(value);

    const { schema: schemaFunc, customProperties, multiSelect, propertyTypes, componentProps } = options?.config as IFieldConfig<IEntityFieldSelectorProps<T>>;

	const onChange = useCallback(
        (option: IDropdownOption | undefined) => {
            if (option === undefined) return;

            let newValue = undefined;

            if (multiSelect) {
                const parsed = (value as Array<string | number>) ?? [];
                const clone = [...parsed];

                if (option.selected) clone.push(option.key);
                else {
                    const currIndex = clone.indexOf(option.key);
                    if (currIndex > -1) {
                        clone.splice(currIndex, 1);
                    }
                }
                newValue = clone;
                setLocalValue(clone as []);
            } else {
                newValue = option.key;
                setLocalValue(newValue);
            }

            onChangeForm(fieldName, newValue);
            validate();
        },
        [multiSelect, onChangeForm, fieldName, value, validate],
    );

	const fields = useMemo(() => schemaHelper.getFields<T>(schemaFunc(), { filterArrayProperties: true })
	, [schemaFunc])

	const properties = useMemo(() => schemaHelper.getCustomProperties<T>(customProperties(), { filterArrayProperties: true })
	, [customProperties])

	const dropdownOptions = useMemo(() => {
		const fieldHeader = [{ key: 'fieldsHeader', text: t("Common.Fields"), itemType: DropdownMenuItemType.Header } as IDropdownOption ];
		const propertiesHeader = [{ key: 'propertiesHeader', text: t("Common.Properties"), itemType: DropdownMenuItemType.Header } as IDropdownOption ];

		const fieldOptions = DropdownOptionEx.toDropdownOptions(fields, _ => _.internalName, _ => _.displayName);
		const customOptions = DropdownOptionEx.toDropdownOptions(properties, _ => `customProperties|${_.internalName}|`, _ => _.displayName);

		let combinedOptions = [];
		if (propertyTypes === 'fields' || propertyTypes === 'both') {
			combinedOptions = combinedOptions.concat(fieldHeader).concat(fieldOptions);
		}
		if (propertyTypes === 'properties' || propertyTypes === 'both') {
			combinedOptions = combinedOptions.concat(propertiesHeader).concat(customOptions)
		}
		return combinedOptions;
	}, [fields, properties, propertyTypes, t])

    return (
        <FilterDropdown
            disabled={disabled}
            multiSelect={multiSelect}
            options={dropdownOptions ?? []}
            selectedKey={multiSelect ? undefined : localValue}
            selectedKeys={multiSelect ? (localValue as string[] | number[]) : undefined}
            onChange={(_, option?: IDropdownOption) => onChange(option)}
            errorMessage={validationMessage}
            onBlur={validate}
            placeholder={'Select..'}
            {...componentProps}
        />
    );
};
